import React, { useState, useRef, useContext } from "react";

import { useNavigate } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { C2, H2, Carousel, ImageUploadModal } from "../../common/commonComps";
import { uploadImage } from "../../common/utils";
import { Context } from "../../common/context";
import { AccountModal } from "../account/accountPieces";
import type { Size, ContextValue, Config, CarouselItem } from "../../types";

type SmallCarouselData = CarouselItem & { subHeader?: string; text?: string };

const sizes: Partial<Record<Size, { h: string; w: string }>> = { large: { h: `368px`, w: `33%` } };

const Wrap = styled(Box)<{ size: Size }>(({ size }) => ({
    maxHeight: sizes[size]?.h,
    maxWidth: sizes[size]?.w,
    display: size === `large` ? `block` : `none`,
    marginLeft: 20,
    cursor: `pointer`,
}));

interface SmallCarouselProps {
    size: Size;
    editable: boolean;
}

const SmallCarousel = ({ size, editable }: SmallCarouselProps): React.ReactElement => {
    const [editingText, setEditingText] = useState<boolean>(false);
    const [editingSubHeader, setEditingSubHeader] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [fileLink, setFileLink] = useState<string | undefined>();
    const [file, setFile] = useState<File | undefined>();
    const [link, setLink] = useState<string>(``);
    const cx = useContext(Context) as ContextValue;

    const clearModal = () => {
        setLink(``);
        setFile(undefined);
        setFileLink(undefined);
        setModalOpen(false);
        document.body.style.overflow = `auto`;
    };

    const openModal = () => {
        setModalOpen(true);
        document.body.style.overflow = `hidden`;
    };

    const scRef = useRef<HTMLDivElement>(null);
    const shRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const smallCarousel = cx.getValue
        ? (cx.getValue(`config`) as Config)?.smallCarousel
        : ({ text: `abc`, image: `http://cnn.com/logo` } as SmallCarouselData); // this is for testing because test-renderer dies on context

    const setImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            const f = e.target.files[0];
            setFileLink(URL.createObjectURL(f));
            setFile(f);
        }
    };

    const sendImage = (): void => {
        if (!file) {
            setFile(undefined);
            setFileLink(undefined);
            setModalOpen(false);
            document.body.style.overflow = `auto`;
            cx.setContext({ smallCarousel: { ...smallCarousel, link: link || smallCarousel?.link } });
            setLink(``);
            return;
        }
        uploadImage(file).then(image => {
            setFile(undefined);
            setFileLink(undefined);
            document.body.style.overflow = `auto`;
            setModalOpen(false);
            cx.setContext({ smallCarousel: { ...smallCarousel, image, link: link || smallCarousel?.link } });
            setLink(``);
        });
    };

    return (
        <Wrap
            size={size}
            onClick={() => {
                if (!editable) {
                    if ((smallCarousel?.link || ``).includes(`http`)) {
                        window.open(smallCarousel?.link);
                    } else {
                        navigate(smallCarousel?.link ?? ``);
                    }
                }
            }}
        >
            <AccountModal preventScroll open={modalOpen}>
                <ImageUploadModal
                    {...{ fileLink, context: smallCarousel, clearModal, link, setLink, sendImage, setImage }}
                />
            </AccountModal>
            <div onClick={() => (editable ? openModal() : (() => {})())}>
                <Carousel src={smallCarousel?.image ?? ``} />
            </div>
            <H2
                style={{ height: `auto` }}
                ref={shRef}
                contentEditable={editingSubHeader}
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: smallCarousel?.subHeader ?? `` }}
                onBlur={v => {
                    cx.setContext({ smallCarousel: { ...smallCarousel, subHeader: v.target.innerHTML } });
                    setEditingSubHeader(false);
                }}
                {...(editable && !editingText
                    ? {
                          onClick: () => {
                              setEditingSubHeader(true);
                              setTimeout(() => {
                                  shRef.current?.focus();
                              }, 0);
                          },
                      }
                    : {})}
                className={editingSubHeader ? `editingText` : editable ? `edit` : ``}
            />
            <C2
                ref={scRef}
                contentEditable={editingText}
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: smallCarousel?.text ?? `` }}
                onBlur={v => {
                    cx.setContext({ smallCarousel: { ...smallCarousel, text: v.target.innerHTML } });
                    setEditingText(false);
                }}
                {...(editable && !editingText
                    ? {
                          onClick: () => {
                              setEditingText(true);
                              setTimeout(() => {
                                  scRef.current?.focus();
                              }, 0);
                          },
                      }
                    : {})}
                className={editingText ? `editingText` : editable ? `edit` : ``}
            />
        </Wrap>
    );
};

export default SmallCarousel;
